import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ZoomTable, ZoomBtn, ZoomModal, ZoomField } from './zoom/ZoomTable';

// Greetings & recordings (2026-10-23): the organisation's uploaded greetings.
// Play, rename, replace the audio (everything using it plays the new audio) and
// delete — a recording that is still in use cannot be deleted.
const err = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';
const secs = (v: any) => { const n = Math.round(Number(v) || 0); return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`; };

export default function RecordingsLibraryPage() {
  const [list, setList] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState('');
  const [edit, setEdit] = useState<any>(null);       // { id?, name, file }
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState<number | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);

  const load = () => axios.get('/api/recordings-library').then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => setMsg(err(e)));
  useEffect(() => { load(); return () => { audio.current?.pause(); }; }, []);

  const play = async (p: any) => {
    if (playing === p.id) { audio.current?.pause(); setPlaying(null); return; }
    try {
      audio.current?.pause();
      const r = await axios.get(`/api/recordings-library/${p.id}/audio`, { responseType: 'blob' });
      const a = new Audio(URL.createObjectURL(r.data)); audio.current = a; setPlaying(p.id);
      a.onended = () => setPlaying(null); await a.play();
    } catch (e) { setMsg('Could not play this recording'); setPlaying(null); }
  };
  const save = async () => {
    setMsg('');
    if (!edit.name.trim()) return setMsg('Give the recording a name');
    if (!edit.id && !edit.file) return setMsg('Choose an audio file (MP3 or WAV)');
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('name', edit.name.trim()); if (edit.file) fd.append('file', edit.file);
      if (edit.id) await axios.put(`/api/recordings-library/${edit.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await axios.post('/api/ivr-prompts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEdit(null); load();
    } catch (e) { setMsg(err(e)); }
    setBusy(false);
  };
  const del = async (p: any) => {
    if (p.used_by?.length) { setMsg(`"${p.name}" is still used by: ${p.used_by.join(', ')}. Choose another greeting there first.`); return; }
    if (!window.confirm(`Delete the recording "${p.name}"?`)) return;
    try { await axios.delete(`/api/recordings-library/${p.id}`); load(); } catch (e) { setMsg(err(e)); }
  };

  const rows = list.filter((p) => !search.trim() || `${p.name} ${p.customer || ''}`.toLowerCase().includes(search.toLowerCase()));
  const columns: any[] = [
    { key: 'name', label: 'Recording', render: (p: any) => <div><div className="z-tbl-primary-cell">{p.name}</div>{p.customer && <div className="z-tbl-sub">{p.customer}</div>}</div> },
    { key: 'len', label: 'Length', render: (p: any) => secs(p.duration_sec) },
    { key: 'used', label: 'Used by', render: (p: any) => p.used_by?.length ? <div style={{ fontSize: 12 }}>{p.used_by.join(', ')}</div> : <span className="z-tbl-sub">Not used</span> },
    { key: 'when', label: 'Uploaded', render: (p: any) => p.created_at ? new Date(p.created_at).toLocaleDateString() : '—' },
    { key: 'play', label: '', render: (p: any) => <ZoomBtn onClick={() => play(p)}>{playing === p.id ? '■ Stop' : '▶ Play'}</ZoomBtn> },
  ];

  return (
    <>
      {msg && <div style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13 }}>{msg} <a href="#" onClick={(e) => { e.preventDefault(); setMsg(''); }}>dismiss</a></div>}
      {edit && (
        <ZoomModal title={edit.id ? `Edit "${edit.orig}"` : 'Upload a recording'} onClose={() => setEdit(null)}
          footer={<><ZoomBtn onClick={() => setEdit(null)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={() => !busy && save()}>{busy ? 'Saving…' : 'Save'}</ZoomBtn></>}>
          <ZoomField label="Name"><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="e.g. Eid holiday (EN)" /></ZoomField>
          <ZoomField label={edit.id ? 'Replace the audio (optional)' : 'Audio file'} hint={edit.id ? 'Everything that uses this recording will play the new audio straight away.' : 'MP3, WAV or M4A, up to 10 MB — converted to phone quality.'}>
            <input type="file" accept="audio/*,.mp3,.wav,.m4a" onChange={(e) => setEdit({ ...edit, file: (e.target.files && e.target.files[0]) || null })} />
          </ZoomField>
        </ZoomModal>)}
      <ZoomTable
        title="Greetings & recordings"
        subtitle="All greetings uploaded for queues, numbers, menus and holidays. Play, rename or replace a recording here; a recording that is in use cannot be deleted."
        search={search} onSearch={setSearch} searchPlaceholder="Search recordings"
        actions={<ZoomBtn variant="primary" onClick={() => { setMsg(''); setEdit({ name: '', file: null }); }}>+ Upload recording</ZoomBtn>}
        columns={columns} rows={rows} rowKey={(p: any) => p.id}
        rowActions={(p: any) => <>
          <ZoomBtn onClick={() => { setMsg(''); setEdit({ id: p.id, orig: p.name, name: p.name, file: null }); }}>Edit</ZoomBtn>
          <ZoomBtn variant="danger" onClick={() => del(p)}>Delete</ZoomBtn>
        </>}
        emptyText="No recordings yet — upload one"
      />
    </>
  );
}
